/**
 * Pre-computed `fontEmbedCSS` for the Insights snapshot captures.
 *
 * `html-to-image`'s built-in font discovery iterates `document.styleSheets`
 * and reads `.cssRules` on each, which throws `SecurityError` for any
 * cross-origin stylesheet (notably Google Fonts). The library logs the error
 * but the captured PNG ends up without the cross-origin `@font-face`
 * declarations embedded — so brand fonts (Inter, Stolzl) fall back to system
 * fonts in the shared image.
 *
 * This module walks the same stylesheets but uses `fetch()` to read the text
 * of cross-origin sheets directly (CORS only blocks `CSSStyleSheet.cssRules`,
 * not GET requests), then inlines each `url(...)` font file as a base64 data
 * URL. The result is a single CSS string suitable for `toBlob`'s
 * `fontEmbedCSS` option, which fully replaces the library's discovery path.
 *
 * Cached at module scope so the network cost is paid once per session.
 */

let cache: Promise<string | undefined> | null = null

export function loadSnapshotFontEmbedCSS(): Promise<string | undefined> {
  if (cache == null) {
    cache = buildSnapshotFontEmbedCSS().catch(() => undefined)
  }
  return cache
}

async function buildSnapshotFontEmbedCSS(): Promise<string | undefined> {
  const fontFaceCSS = await collectFontFaceRules()
  if (fontFaceCSS.length === 0) return undefined
  return fontFaceCSS.join('\n')
}

async function collectFontFaceRules(): Promise<string[]> {
  const sheets = Array.from(document.styleSheets)
  const collected = await Promise.all(sheets.map(extractFontFaceCSS))
  return collected.flat()
}

async function extractFontFaceCSS(sheet: CSSStyleSheet): Promise<string[]> {
  const baseHref = sheet.href ?? document.baseURI
  try {
    const rules = Array.from(sheet.cssRules)
    const fontFaceRules = rules.filter(isFontFaceRule)
    return Promise.all(
      fontFaceRules.map((rule) => inlineFontUrlsInCSS(rule.cssText, baseHref)),
    )
  } catch {
    // Cross-origin sheet: `cssRules` access is blocked, but a plain GET of
    // the stylesheet URL succeeds (no CORS preflight for `text/css`).
    const href = sheet.href
    if (!href) return []
    try {
      const text = await fetch(href).then((res) => res.text())
      const matches = text.match(FONT_FACE_BLOCK_REGEX) ?? []
      return Promise.all(
        matches.map((block) => inlineFontUrlsInCSS(block, href)),
      )
    } catch {
      return []
    }
  }
}

function isFontFaceRule(rule: CSSRule): rule is CSSFontFaceRule {
  return rule.type === CSSRule.FONT_FACE_RULE
}

const FONT_FACE_BLOCK_REGEX = /@font-face\s*\{[^}]*\}/g
const URL_REGEX = /url\(\s*(['"]?)([^)'"]+)\1\s*\)/g

async function inlineFontUrlsInCSS(
  css: string,
  baseHref: string,
): Promise<string> {
  const matches = Array.from(css.matchAll(URL_REGEX))

  const replacements = await Promise.all(
    matches.map(async (m) => {
      const originalUrl = m[2]
      const resolvedUrl = resolveUrl(originalUrl, baseHref)
      if (!/^https?:\/\//.test(resolvedUrl)) return null
      try {
        const res = await fetch(resolvedUrl)
        if (!res.ok) return null
        const blob = await res.blob()
        const dataUrl = await blobToDataUrl(blob)
        return { originalUrl, dataUrl }
      } catch {
        return null
      }
    }),
  )

  if (replacements.every((r) => r == null)) return css

  let result = css
  for (const replacement of replacements) {
    if (replacement == null) continue
    const { originalUrl, dataUrl } = replacement
    // Replace the URL as it appears in the CSS, preserving the `url(...)`
    // wrapper and quote style. Using the original literal (rather than the
    // resolved absolute form) keeps relative URLs in same-origin sheets
    // matching correctly.
    result = result.replace(
      new RegExp(`url\\(\\s*(['"]?)${escapeRegex(originalUrl)}\\1\\s*\\)`, 'g'),
      `url(${dataUrl})`,
    )
  }
  return result
}

function resolveUrl(url: string, base: string): string {
  try {
    return new URL(url, base).href
  } catch {
    return url
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.addEventListener('load', () => {
      const { result } = reader
      if (typeof result === 'string') resolve(result)
      else reject(new Error('FileReader returned a non-string result'))
    })
    reader.addEventListener('error', () => {
      reject(reader.error ?? new Error('FileReader failed'))
    })
    reader.readAsDataURL(blob)
  })
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
